import React, { useCallback, useEffect, useState } from 'react';
import { Users, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { teamAccessAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { InviteMemberButton, SendAccessButton } from './TeamAccessButtons';
import { PreviewSyncCard } from './PreviewSyncCard';

const ROLE_OPTIONS = [['member', 'Equipo (sin CRM ni Finanzas)'], ['ceo', 'CEO']];
const ROLE_LABEL = { ceo: 'CEO', member: 'Equipo', manager: 'Manager (antiguo)' };

const EditUserDialog = ({ u, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: u.name || '', email: u.email || '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await teamAccessAPI.updateUser(u.id, form); toast.success('Usuario actualizado'); onSaved(); onClose(); }
    catch (e) { toast.error(e.message || 'No se pudo guardar'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Editar {u.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email" />
            <p className="text-xs text-muted-foreground mt-1">Con este email entra al portal. Si lo cambias, tendrá que usar el nuevo.</p></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy || !form.name.trim()} data-testid="edit-user-save">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, isSelf, onChange, onEdit }) => (
  <div className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border ${u.is_active ? '' : 'opacity-60 bg-muted/40'}`} data-testid={`user-row-${u.id}`}>
    <div className="flex-1 min-w-[200px]">
      <p className="font-medium text-sm">{u.name}{isSelf && <span className="text-xs text-muted-foreground"> (tú)</span>}</p>
      <p className="text-xs text-muted-foreground">{u.email}</p>
    </div>
    {u.pending_setup && <Badge variant="outline" className="text-amber-700 border-amber-300">Sin contraseña aún</Badge>}
    <Select value={u.role} disabled={isSelf} onValueChange={(v) => onChange(u, { role: v })}>
      <SelectTrigger className="h-9 w-[220px] text-sm" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {u.role === 'manager' && <SelectItem value="manager">{ROLE_LABEL.manager}</SelectItem>}
        {ROLE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
    <div className="flex items-center gap-2 text-xs">
      <Switch checked={u.is_active} disabled={isSelf} onCheckedChange={(v) => onChange(u, { is_active: v })} data-testid={`user-active-${u.id}`} />
      <span data-testid={`user-status-${u.id}`}>{u.is_active ? 'Activo' : 'Desactivado'}</span>
    </div>
    <Button size="sm" variant="ghost" onClick={() => onEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4" /></Button>
    {!isSelf && u.is_active && <SendAccessButton member={u} />}
  </div>
);

export default function UsersRolesPage() {
  const { user } = useAuthStore();
  const [users, setUsers] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => teamAccessAPI.listUsers().then(setUsers).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const change = async (u, patch) => {
    if (patch.is_active === false && !window.confirm(`¿Desactivar a ${u.name}? Dejará de poder entrar al momento. Sus tareas no se borran.`)) return;
    if (patch.role && !window.confirm(`¿Cambiar el rol de ${u.name} a ${ROLE_LABEL[patch.role]}?`)) return;
    try { await teamAccessAPI.updateUser(u.id, patch); toast.success('Cambio guardado'); load(); }
    catch (e) { toast.error(e.message || 'No se pudo guardar'); }
  };

  return (
    <div className="space-y-6" data-testid="users-roles-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Users className="w-7 h-7 text-primary" />Usuarios y Roles</h1>
          <p className="text-sm text-muted-foreground mt-1">Cuentas del equipo. Los clientes se gestionan desde su ficha.</p>
        </div>
        <InviteMemberButton onDone={load} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Equipo</CardTitle>
          <CardDescription>CEO: ve todo. Equipo: no ve CRM, Finanzas, Objetivos ni Equipo. Los cambios se aplican al momento.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {!users ? <Loader2 className="w-5 h-5 animate-spin" /> : users.map((u) => (
            <UserRow key={u.id} u={u} isSelf={u.id === user?.id} onChange={change} onEdit={setEditing} />
          ))}
        </CardContent>
      </Card>
      {user?.role === 'ceo' && <PreviewSyncCard />}
      {editing && <EditUserDialog u={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
